import { FC, ReactNode, useEffect, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Button } from "src/domains/shared/components/Button";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import { useTimezone } from "src/domains/shared/hooks/useTimezone";
import { formatInstant } from "src/domains/shared/utils/datetime";
import { HistoryEvent } from "src/domains/myPageContent/projectHistory/history-query/projectHistory.model";
import { groupHistoryEventsByDate } from "src/domains/myPageContent/projectHistory/history-query/projectHistory.selectors";
import { BranchFade, CAPTION, SkeletonBone, SkeletonReveal, StateSlot, SUBTLE_SURFACE } from "src/domains/shared/components/StateTransition";
import { useHistorySentinelPagination } from "../hooks/useHistorySentinelPagination";
import { HistoryTimelineItem } from "./HistoryTimelineItem";

interface HistoryTimelineListProps {
  items: HistoryEvent[];
  /** 지금 보여줘야 할 대상의 데이터가 아직 없음 — 첫 로딩, 또는 프로젝트 전환 직후 구간 */
  isLoading: boolean;
  /** 비멤버 404 — 자동 재시도 없음, 재시도 버튼도 없음 */
  isForbidden: boolean;
  /**
   * 404 이외의 조회 실패. **표시할 항목이 하나도 없을 때만** 실패 칸으로 전환한다.
   * 받아둔 페이지가 있는데 다음 페이지만 실패한 경우는 목록을 두고 목록 끝 칸의
   * "더 불러오기" 가 복구를 맡는다.
   */
  isGeneralError: boolean;
  /** 종류 필터가 적용된 상태인지 — 빈 결과 문구 분기(empty vs emptyFiltered) */
  isFilterActive: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** react-query v4 fetchNextPage — 실패해도 reject 하지 않고 결과 객체로 resolve 하므로 isError 로 판정한다. */
  fetchNextPage: () => Promise<{ isError: boolean }>;
  /** 전체 재조회. `isGeneralError` 이면서 항목이 하나도 없는 화면에서만 호출된다. */
  onRetry: () => void;
  /**
   * 조회 컨텍스트를 식별하는 키 — 호출부가 `projectHistoryQueryKey` 파생값을 넘긴다.
   * 값이 바뀌면 전환으로 보고 목록 스크롤 위치와 "더 불러오기" 수동 전환 상태를 리셋한다.
   */
  resetKey: string;
}

const LOAD_MORE_SLOT_HEIGHT_PX = 44;
const DATE_HEADER_HEIGHT_PX = 30;
const SKELETON_SENTENCE_WIDTHS = ["62%", "78%", "54%", "70%", "48%", "66%", "74%", "58%", "68%", "52%", "60%", "72%"];

/** 날짜 머리 표기 — `YYYY.MM.DD (요일)`, 요일은 현재 언어·표시 시간대 기준. */
const formatDateHeader = (iso: string, timezone: string, language: string) => {
  const date = formatInstant(iso, "YYYY.MM.DD", { tz: timezone });
  const weekday = new Intl.DateTimeFormat(language, { weekday: "short", timeZone: timezone }).format(new Date(iso));
  return `${date} (${weekday})`;
};

/**
 * 타임라인 목록 — 로딩·권한없음·조회실패·빈결과 상태 칸과 날짜 묶음 목록을 담당한다.
 * 스크롤 컨테이너를 소유하므로 `resetKey` 전환 시의 스크롤 상단 리셋도 여기서 한다.
 * 하단 근접 자동 로드와 실패 시 수동 "더 불러오기" 전환은 `useHistorySentinelPagination` 이 소유한다.
 */
export const HistoryTimelineList: FC<HistoryTimelineListProps> = ({
  items,
  isLoading,
  isForbidden,
  isGeneralError,
  isFilterActive,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onRetry,
  resetKey,
}) => {
  const { t, i18n } = useTranslation("projects");
  const { timezone } = useTimezone();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { sentinelRef, nextPageFailed, handleManualLoadMore } = useHistorySentinelPagination({
    scrollContainerRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    resetKey,
  });

  // 전환 시 스크롤 위치를 상단으로 리셋한다. 상태 칸을 그리는 중이면 ref 가 비어 아무 일도 하지 않는다.
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [resetKey]);

  const showManualLoadMore = !isFetchingNextPage && hasNextPage && nextPageFailed;

  const body: { key: string; node: ReactNode } = (() => {
    if (isLoading) {
      return {
        key: "loading",
        node: (
          <SkeletonReveal label={t("myPageModal.history.timeline", { ns: "projects" })}>
            <Box sx={{ ...dateHeaderSx, position: "static" }}>
              <SkeletonBone width={90} height={9} />
            </Box>
            {SKELETON_SENTENCE_WIDTHS.map((width, index) => (
              <Box key={index} sx={skeletonRowSx}>
                <SkeletonBone width={34} height={9} />
                <SkeletonBone width={8} height={8} radius={4} />
                <SkeletonBone width={width} height={11} />
              </Box>
            ))}
          </SkeletonReveal>
        ),
      };
    }
    if (isForbidden) {
      return {
        key: "forbidden",
        node: <StateSlot tone="neutral" icon="lock" title={t("myPage.history.forbidden", { ns: "projects" })} />,
      };
    }
    // 보여줄 항목이 남아 있는 실패(=다음 페이지 로드 실패)는 목록을 실패 칸으로 덮지 않는다.
    if (isGeneralError && items.length === 0) {
      return {
        key: "error",
        node: (
          <StateSlot
            tone="error"
            icon="alert"
            title={t("myPage.history.error", { ns: "projects" })}
            retryLabel={t("myPage.history.retry", { ns: "projects" })}
            onRetry={onRetry}
          />
        ),
      };
    }
    if (items.length === 0) {
      return {
        key: "empty",
        node: (
          <StateSlot
            tone="info"
            icon="inbox"
            title={t(isFilterActive ? "myPage.history.emptyFiltered" : "myPage.history.empty", { ns: "projects" })}
          />
        ),
      };
    }
    const groups = groupHistoryEventsByDate(items, timezone);
    return {
      key: "list",
      node: (
        <Box ref={scrollContainerRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto", borderRadius: "0 0 10px 10px" }}>
          {groups.map((group) => {
            const headerText = formatDateHeader(group.firstOccurredAt, timezone, i18n.language);
            return (
              <Box component="section" key={group.dateKey} aria-label={headerText}>
                <Typography component="h3" sx={dateHeaderSx}>
                  {headerText}
                </Typography>
                <Box component="ul" role="list" sx={{ margin: 0, padding: 0 }}>
                  {group.events.map((event) => (
                    <HistoryTimelineItem key={event.id} event={event} />
                  ))}
                </Box>
              </Box>
            );
          })}
          {hasNextPage && <Box ref={sentinelRef} aria-hidden="true" sx={{ height: "1px" }} />}
          <Box sx={{ height: LOAD_MORE_SLOT_HEIGHT_PX, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {isFetchingNextPage && (
              <Box role="status" aria-label={t("myPageModal.history.loadingMore", { ns: "projects" })} sx={{ display: "flex", color: COLOR_CONSTANTS.primary.main }}>
                <CircularProgress size={16} thickness={5} color="inherit" />
              </Box>
            )}
            {showManualLoadMore && (
              <Button size="small" emphasis="outlined" intent="inherit" onClick={handleManualLoadMore}>
                {t("myPage.history.loadMore", { ns: "projects" })}
              </Button>
            )}
          </Box>
        </Box>
      ),
    };
  })();

  return <BranchFade key={body.key}>{body.node}</BranchFade>;
};

const dateHeaderSx = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  height: DATE_HEADER_HEIGHT_PX,
  margin: 0,
  display: "flex",
  alignItems: "center",
  padding: "0 16px",
  backgroundColor: SUBTLE_SURFACE,
  borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.03em",
  color: CAPTION,
  fontVariantNumeric: "tabular-nums",
} as const;

const skeletonRowSx = {
  height: 40,
  display: "grid",
  gridTemplateColumns: "44px 8px minmax(0, 1fr)",
  alignItems: "center",
  columnGap: "10px",
  padding: "0 16px",
  borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
} as const;
